import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { apiGet, apiPostForm, ApiError } from '../lib/api.ts'

type UserRole = 'locum' | 'practice'

interface AuthUser {
  id: number
  email: string
  role: UserRole
  display_name: string
}

interface AuthContextType {
  user: AuthUser | null
  loading: boolean
  login: (email: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true

    const loadSession = async () => {
      try {
        const data = await apiGet<{ authenticated: boolean; user: AuthUser | null }>('/auth/me.php?format=json')
        if (!mounted) {
          return
        }

        if (data.authenticated && data.user && (data.user.role === 'locum' || data.user.role === 'practice')) {
          setUser(data.user)
        } else {
          setUser(null)
        }
      } catch (error) {
        if (!mounted) {
          return
        }

        if (!(error instanceof ApiError && error.status === 401)) {
          console.error(error)
        }
        setUser(null)
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    loadSession()

    return () => {
      mounted = false
    }
  }, [])

  const login = async (email: string, password: string): Promise<boolean> => {
    const data = await apiPostForm<{ success: boolean; user: AuthUser | null }>(
      '/auth/login.php?format=json',
      { email, password }
    )

    if (data.success && data.user && (data.user.role === 'locum' || data.user.role === 'practice')) {
      setUser(data.user)
      return true
    }

    return false
  }

  const logout = async () => {
    try {
      await apiPostForm<{ success: boolean }>('/auth/logout.php?format=json', {})
    } catch (error) {
      console.error(error)
    }
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}
